import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Calendar, User, Phone, Mail, Euro, Clock, 
  Edit, Trash2, Check, X, Loader2, Bell, Volume2,
  Home, Users, MessageSquare, CreditCard, Banknote,
  RefreshCw, Filter, Search
} from 'lucide-react';
import api from '../../lib/api';

export default function BookingsManagementPage() {
  const { selectedProperty } = useOutletContext() || {};
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sound alert
  const audioRef = useRef(null);
  const lastBookingCount = useRef(0);

  useEffect(() => {
    loadData();
    // Poll for new bookings every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [selectedProperty]);

  // Play sound when new booking arrives
  useEffect(() => {
    if (bookings.length > lastBookingCount.current && lastBookingCount.current > 0) {
      playNotificationSound();
      toast.success('🔔 Nouvelle réservation reçue !');
    }
    lastBookingCount.current = bookings.length;
  }, [bookings.length]);

  const playNotificationSound = () => {
    try {
      // Create audio context for notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Nice notification melody
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      
      oscillator.start(audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1); // C#6
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.2); // A5
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const loadData = async () => {
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        api.get('/admin/bookings'),
        api.get('/admin/booking-stats')
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (booking) => {
    setSelectedBooking(booking);
    setEditForm({
      guest_name: booking.guest_name,
      guest_email: booking.guest_email,
      guest_phone: booking.guest_phone,
      check_in: booking.check_in,
      check_out: booking.check_out,
      guests_count: booking.guests_count,
      total_price: booking.total_price,
      status: booking.status,
      payment_status: booking.payment_status
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/bookings/${selectedBooking.id}`, editForm);
      toast.success('Réservation mise à jour');
      playNotificationSound(); // Sound alert on modification
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bookingId) => {
    if (!confirm('Supprimer cette réservation ?')) return;
    
    try {
      await api.delete(`/admin/bookings/${bookingId}`);
      toast.success('Réservation supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleQuickStatus = async (bookingId, status) => {
    try {
      await api.put(`/admin/bookings/${bookingId}`, { status });
      toast.success('Statut mis à jour');
      playNotificationSound(); // Sound alert
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleQuickPayment = async (bookingId, paymentStatus) => {
    try {
      await api.put(`/admin/bookings/${bookingId}`, { payment_status: paymentStatus });
      toast.success('Paiement mis à jour');
      playNotificationSound(); // Sound alert
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return format(new Date(dateStr), 'd MMM yyyy', { locale: fr });
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { label: 'En attente', className: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30' },
      confirmed: { label: 'Confirmée', className: 'bg-green-500/20 text-green-500 border-green-500/30' },
      cancelled: { label: 'Annulée', className: 'bg-red-500/20 text-red-500 border-red-500/30' },
      rejected: { label: 'Refusée', className: 'bg-red-500/20 text-red-500 border-red-500/30' },
    };
    const config = configs[status] || configs.pending;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const getPaymentBadge = (status, method) => {
    if (status === 'paid') {
      return <Badge className="bg-green-500/20 text-green-500 border-green-500/30">Payé</Badge>;
    }
    return (
      <Badge variant="outline" className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">
        {method === 'cash' ? 'Espèces' : 'En attente'}
      </Badge>
    );
  };

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    const matchesFilter = filter === 'all' || booking.status === filter;
    const matchesSearch = !searchQuery || 
      booking.guest_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.guest_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.property_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold">Réservations Voyageurs</h1>
          <p className="text-muted-foreground text-sm">Gérez les demandes de réservation directes</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-yellow-500/10 border-yellow-500/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Check className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.confirmed}</p>
                  <p className="text-xs text-muted-foreground">Confirmées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-primary/10 border-primary/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Euro className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold">€{stats.total_revenue?.toFixed(0) || 0}</p>
                  <p className="text-xs text-muted-foreground">Revenus</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_bookings}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom, email..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Tabs value={filter} onValueChange={setFilter} className="w-full sm:w-auto">
          <TabsList>
            <TabsTrigger value="all">Toutes</TabsTrigger>
            <TabsTrigger value="pending">En attente</TabsTrigger>
            <TabsTrigger value="confirmed">Confirmées</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Aucune réservation</h3>
              <p className="text-muted-foreground text-sm">
                Les réservations des voyageurs apparaîtront ici.
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredBookings.map((booking) => (
            <Card key={booking.id} className="hover:border-primary/30 transition-colors">
              <CardContent className="pt-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Main Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-4 w-4 text-primary" />
                          <span className="font-semibold">{booking.guest_name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {booking.guest_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {booking.guest_phone}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {getStatusBadge(booking.status)}
                        {getPaymentBadge(booking.payment_status, booking.payment_method)}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <Home className="h-4 w-4 text-muted-foreground" />
                        {booking.property_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {formatDate(booking.check_in)} → {formatDate(booking.check_out)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        {booking.guests_count} pers.
                      </span>
                      <span className="flex items-center gap-1">
                        <Euro className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold text-primary">€{booking.total_price?.toFixed(2)}</span>
                      </span>
                    </div>

                    {booking.message && (
                      <p className="text-sm text-muted-foreground flex items-start gap-2">
                        <MessageSquare className="h-4 w-4 shrink-0 mt-0.5" />
                        {booking.message}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 lg:flex-col lg:w-auto">
                    {booking.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleQuickStatus(booking.id, 'confirmed')}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <Check className="h-4 w-4 mr-1" /> Confirmer
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleQuickStatus(booking.id, 'rejected')}
                          className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                        >
                          <X className="h-4 w-4 mr-1" /> Refuser
                        </Button>
                      </>
                    )}
                    {booking.payment_status === 'pending' && booking.status !== 'cancelled' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleQuickPayment(booking.id, 'paid')}
                        className="border-primary/50"
                      >
                        <CreditCard className="h-4 w-4 mr-1" /> Marquer payé
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(booking)}
                    >
                      <Edit className="h-4 w-4 mr-1" /> Modifier
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      onClick={() => handleDelete(booking.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier la réservation</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  value={editForm.guest_name || ''}
                  onChange={(e) => setEditForm({ ...editForm, guest_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={editForm.guest_phone || ''}
                  onChange={(e) => setEditForm({ ...editForm, guest_phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={editForm.guest_email || ''}
                onChange={(e) => setEditForm({ ...editForm, guest_email: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Arrivée</Label>
                <Input
                  type="date"
                  value={editForm.check_in || ''}
                  onChange={(e) => setEditForm({ ...editForm, check_in: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Départ</Label>
                <Input
                  type="date"
                  value={editForm.check_out || ''}
                  onChange={(e) => setEditForm({ ...editForm, check_out: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Voyageurs</Label>
                <Input
                  type="number"
                  min="1"
                  value={editForm.guests_count || 1}
                  onChange={(e) => setEditForm({ ...editForm, guests_count: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Prix total (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.total_price || 0}
                  onChange={(e) => setEditForm({ ...editForm, total_price: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="confirmed">Confirmée</SelectItem>
                    <SelectItem value="cancelled">Annulée</SelectItem>
                    <SelectItem value="rejected">Refusée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Paiement</Label>
                <Select
                  value={editForm.payment_status}
                  onValueChange={(value) => setEditForm({ ...editForm, payment_status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="paid">Payé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
